// Explore-stats page: Party timeline — start and end dates.
// A Gantt timeline of every party's representation span (start → end date),
// with a Parliament selector (default: all) that narrows to the parties
// represented during a single Parliament, plus a downloadable table of party
// name, ID and start/end dates.

import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Box, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import GanttTimeline from './GanttTimeline';

export default function PartyTimeline() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parliament, setParliament] = useState('all');

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const params = {};
        if (parliament && parliament !== 'all') params.parliament = parliament;
        const res = await getExploreStat('party-timeline', params);
        if (active) { setData(res); setError(null); }
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parliament]);

  const charts = data?.charts || {};

  return (
    <ExploreStatsPage
      title="Party timeline"
      description="Every party represented in the Commonwealth Parliament, with the start and end date of its representation. A party's span runs from the opening of the first Parliament it sat in to the close of the last; a party still in the current Parliament is shown as ongoing. Use the selector to narrow to the parties represented in a single Parliament."
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="party-timeline"
    >
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>Parliament</InputLabel>
          <Select label="Parliament" value={parliament}
                  onChange={(e) => setParliament(e.target.value)}>
            <MenuItem value="all">All parliaments</MenuItem>
            {(charts.parliaments || []).map((p) => (
              <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {data && (
        <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {parliament === 'all'
              ? `Party representation timeline — ${charts.total || 0} parties`
              : `Parties represented — ${charts.total || 0}`}
          </Typography>
          <GanttTimeline items={charts.timeline || []} />
        </Paper>
      )}
    </ExploreStatsPage>
  );
}
